import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { Activity, ArrowRight, ArrowRightLeft, ChartNoAxesCombined, Search, SearchCode, X } from "lucide-react";
import {
  ResourceCover,
  RESOURCE_HUB_THEMES,
  inferResourceCoverCluster,
  type ResourceHub,
} from "@/components/resources/ResourceCover";
import type { Locale } from "@/lib/i18n";
import type { ResourceCluster, ResourceContentType } from "@/lib/resource-commerce";
import { getResourceEditorialStrategy } from "@/lib/resource-editorial-strategy";

export type EditorialResource = {
  id?: string | number;
  slug: string;
  slug_es?: string | null;
  title_en?: string | null;
  title_es?: string | null;
  excerpt_en?: string | null;
  excerpt_es?: string | null;
  tags_en?: string[] | null;
  tags_es?: string[] | null;
  cover_image_url?: string | null;
  cover_image_url_es?: string | null;
  reading_minutes?: number | null;
  updated_at?: string | null;
  reviewed_at?: string | null;
  cluster?: ResourceCluster | null;
  content_type?: ResourceContentType | null;
};

export function resourceHref(resource: EditorialResource, locale: Locale) {
  return locale === "es"
    ? `/es/recursos/${resource.slug_es ?? resource.slug}`
    : `/resources/${resource.slug}`;
}

export function resourceTitle(resource: EditorialResource, locale: Locale) {
  const strategy = getResourceEditorialStrategy(resource);
  return locale === "es"
    ? strategy.displayTitle?.es || resource.title_es || resource.title_en || resource.slug
    : strategy.displayTitle?.en || resource.title_en || resource.slug;
}

export function resourceExcerpt(resource: EditorialResource, locale: Locale) {
  const strategy = getResourceEditorialStrategy(resource);
  return locale === "es"
    ? strategy.dek?.es || resource.excerpt_es || resource.excerpt_en || ""
    : strategy.dek?.en || resource.excerpt_en || "";
}

export function resourceTags(resource: EditorialResource, locale: Locale) {
  return ((locale === "es" && resource.tags_es?.length ? resource.tags_es : resource.tags_en) ?? []) as string[];
}

function hubStyle(hub: ResourceHub) {
  const theme = RESOURCE_HUB_THEMES[hub];
  return {
    "--hub-accent": theme.accent,
    "--hub-accent-ink": theme.accentInk,
  } as CSSProperties;
}

export function ResourceCard({
  resource,
  locale,
  updated,
  readLabel,
}: {
  resource: EditorialResource;
  locale: Locale;
  updated?: string;
  readLabel: string;
}) {
  const hub = inferResourceCoverCluster(resource, locale);
  const theme = RESOURCE_HUB_THEMES[hub];
  const Icon = theme.Icon;
  const tag = resourceTags(resource, locale)[0] ?? "";

  return (
    <Link
      to={resourceHref(resource, locale)}
      data-resource-hub={hub}
      style={hubStyle(hub)}
      className="resource-card group flex h-full flex-col overflow-hidden rounded-[1.5rem] border bg-[var(--surface-editorial)] shadow-[0_22px_64px_-52px_rgb(10_10_10/0.55)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_80px_-50px_rgb(10_10_10/0.58)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10 bg-[var(--surface-authority)]">
        <ResourceCover resource={resource} locale={locale} compact className="transition-transform duration-700 group-hover:scale-[1.025]" />
      </div>
      <div className="relative flex flex-1 flex-col p-6">
        <div className="flex items-center justify-between gap-4">
          <p className="resource-card__hub inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em]">
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-lg border"
              style={{
                borderColor: `color-mix(in srgb, ${theme.accent} 42%, transparent)`,
                backgroundColor: `color-mix(in srgb, ${theme.accent} 12%, transparent)`,
                color: theme.accentInk,
              }}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            {theme.label[locale]}
          </p>
          {tag ? <span className="max-w-[45%] truncate text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{tag}</span> : null}
        </div>

        <h3 className="mt-4 font-display text-xl font-semibold leading-tight tracking-tight text-foreground">
          {resourceTitle(resource, locale)}
        </h3>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {locale === "es" ? "Por" : "By"} <span className="text-foreground/80">Samuel Noriega</span>
          {updated ? <><span aria-hidden className="mx-1.5">·</span>{locale === "es" ? "Actualizado" : "Updated"} {updated}</> : null}
        </p>
        {resourceExcerpt(resource, locale) ? (
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
            {resourceExcerpt(resource, locale)}
          </p>
        ) : null}
        <div className="mt-auto flex items-center justify-between border-t border-[var(--line-neutral)] pt-5 text-xs text-muted-foreground">
          <span>{resource.reading_minutes ?? 0} min</span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
            {readLabel}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export type TopicGatewayItem = {
  label: string;
  title: string;
  body: string;
  href: string;
  serviceHref: string;
  serviceLabel: string;
  /** Legacy taxonomy value retained for CMS and call-site compatibility. */
  accent: "teal" | "bronze" | "indigo" | "sage";
};

type GatewayHub = "performance" | "conversion" | "technical-seo" | "migration";

const LEGACY_HUB_MAP: Record<TopicGatewayItem["accent"], GatewayHub> = {
  teal: "performance",
  bronze: "conversion",
  indigo: "technical-seo",
  sage: "migration",
};

const CHAPTER_CODES: Record<TopicGatewayItem["accent"], string> = {
  teal: "01",
  bronze: "02",
  indigo: "03",
  sage: "04",
};

const HUB_ICONS: Record<GatewayHub, typeof Activity> = {
  performance: Activity,
  conversion: ChartNoAxesCombined,
  "technical-seo": SearchCode,
  migration: ArrowRightLeft,
};

export function TopicGateway({ item, exploreLabel }: { item: TopicGatewayItem; exploreLabel: string }) {
  const hub = LEGACY_HUB_MAP[item.accent];
  const theme = RESOURCE_HUB_THEMES[hub];
  const Icon = HUB_ICONS[hub];

  return (
    <article
      data-resource-hub={hub}
      style={hubStyle(hub)}
      className="resource-hub-card flex h-full flex-col p-7"
    >
      <div className="relative flex items-start justify-between gap-4">
        <span className="resource-hub-icon-wrap" aria-hidden="true">
          <Icon className="h-6 w-6" />
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">SGT / {CHAPTER_CODES[item.accent]}</span>
      </div>

      <div className="relative mt-8">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: theme.accentInk }}>
          {item.label}
        </p>
        <h3 className="mt-5 max-w-[96%] font-display text-[2rem] font-semibold leading-[1.05] tracking-tight text-foreground">
          {item.title}
        </h3>
        <p className="mt-6 max-w-[94%] text-base leading-relaxed text-muted-foreground">{item.body}</p>
      </div>

      <div className="relative mt-auto flex flex-col gap-4 pt-10">
        <Link
          to={item.href}
          className="inline-flex items-center justify-between rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background transition-colors hover:bg-foreground/90"
        >
          {exploreLabel}<ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to={item.serviceHref}
          className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/65 transition-colors hover:text-foreground"
        >
          {item.serviceLabel} <span style={{ color: theme.accentInk }}>→</span>
        </Link>
      </div>
    </article>
  );
}

export type ResourceCategory = { key: string; label: string; count: number };

export function ResourceFilterBar({
  categories,
  active,
  query,
  onCategory,
  onQuery,
  locale,
}: {
  categories: ResourceCategory[];
  active: string;
  query: string;
  onCategory: (key: string) => void;
  onQuery: (query: string) => void;
  locale: Locale;
}) {
  return (
    <div className="flex flex-col gap-4 border-y border-border py-6 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap gap-2" role="group" aria-label={locale === "es" ? "Categorías" : "Categories"}>
        {categories.map((category) => {
          const selected = category.key === active;
          return (
            <button
              key={category.key}
              type="button"
              aria-pressed={selected}
              onClick={() => onCategory(category.key)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${selected ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:border-foreground/35 hover:text-foreground"}`}
            >
              {category.label}<span className="ml-2 opacity-70">{category.count}</span>
            </button>
          );
        })}
      </div>
      <div className="relative w-full lg:w-80" role="search">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={locale === "es" ? "Buscar guías…" : "Search guides…"}
          className="w-full rounded-full border border-border bg-background py-2.5 pl-9 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
        />
        {query ? (
          <button type="button" onClick={() => onQuery("")} aria-label={locale === "es" ? "Limpiar búsqueda" : "Clear search"} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground hover:bg-card hover:text-foreground">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
}
